/**
 * HMAC-SHA256 request signing for the shared Axios client (Requirements 3.1-3.7).
 *
 * Everything except `signRequest`, `sha256Hex`, and `hmacSha256Hex` is pure, and the
 * two crypto helpers are deterministic functions of their inputs. `signRequest`
 * takes `secret`, `now`, and `nonce` as injectable options so a signature can be
 * reproduced exactly under test.
 *
 * ## Security note — read before trusting this
 *
 * Req 3.6 puts the signing secret in `REACT_APP_HMAC_SECRET`. CRA inlines every
 * `REACT_APP_*` value into the production bundle at build time, so the secret ships
 * to every browser and is readable by anyone who opens DevTools. This deters casual
 * tampering and satisfies the backend's current contract, but it does **not**
 * authenticate the caller: any client can compute a valid signature.
 *
 * The correct long-term shape is a BFF/proxy that holds the secret server-side. This
 * module exists as a single seam so that move is a one-file change — the interceptor
 * calls `signRequest(config)` either way.
 *
 * ## Canonical string
 *
 * Exactly five `\n`-joined lines (Req 3.4):
 *
 *   METHOD\nPATH\nTIMESTAMP\nNONCE\nBODY_HASH
 *
 * The query string is deliberately excluded, because Req 3.4 defines `PATH` only.
 * Query parameters are therefore not integrity-protected — a known limitation.
 */

/** Methods that carry a signature. GET/HEAD/OPTIONS never do (Req 3.1, 3.7). */
export const SIGNED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Signature headers, named once so callers and tests agree. */
export const SIGNATURE_HEADER = "X-Signature";
export const TIMESTAMP_HEADER = "X-Timestamp";
export const NONCE_HEADER = "X-Nonce";

/**
 * Marks a request whose body could not be hashed (multipart uploads and friends).
 * The signature still covers method, path, timestamp, and nonce.
 */
export const BODY_HASH_SCOPE_HEADER = "X-Body-Hash-Scope";
export const BODY_HASH_SCOPE_NONE = "none";

/** SHA-256 of the empty string — the body hash for every empty body (Req 3.5). */
export const EMPTY_BODY_SHA256 =
  "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855";

/** Env var holding the signing secret (Req 3.6). */
export const HMAC_SECRET_ENV_VAR = "REACT_APP_HMAC_SECRET";

/** Nonce floor from Req 3.3: 16 characters of hex text, i.e. 8 random bytes. */
export const MIN_NONCE_BYTES = 8;
const DEFAULT_NONCE_BYTES = 16; // 32 hex characters

/**
 * Raised when signing is impossible for configuration reasons — currently only a
 * missing `crypto.subtle`, which happens in an insecure context (plain HTTP on a
 * non-localhost origin). Failing loudly beats silently sending unsigned mutations.
 */
export class HmacConfigurationError extends Error {
  constructor(message) {
    super(message);
    this.name = "HmacConfigurationError";
    this.code = "HMAC_CONFIGURATION";
  }
}

/* ------------------------------------------------------------------ *
 * Method scoping
 * ------------------------------------------------------------------ */

/** Upper-cased method name, or "" when there is nothing readable. */
function normalizeMethod(method) {
  if (typeof method !== "string") return "";
  return method.trim().toUpperCase();
}

/**
 * True when the method must be signed (Req 3.1, 3.7).
 *
 * An absent method is treated as GET, matching Axios's own default.
 *
 * @param {string} [method]
 * @returns {boolean}
 */
export function requiresSignature(method) {
  return SIGNED_METHODS.has(normalizeMethod(method));
}

/* ------------------------------------------------------------------ *
 * Canonical path
 * ------------------------------------------------------------------ */

const ABSOLUTE_SCHEME = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//;

/** Drops the fragment and the query string; neither is part of the path. */
function stripQueryAndHash(value) {
  let raw = value;
  const hashIndex = raw.indexOf("#");
  if (hashIndex !== -1) raw = raw.slice(0, hashIndex);
  const queryIndex = raw.indexOf("?");
  if (queryIndex !== -1) raw = raw.slice(0, queryIndex);
  return raw;
}

/** True for `https://host/...` and protocol-relative `//host/...` forms. */
function isAbsoluteUrl(value) {
  return ABSOLUTE_SCHEME.test(value) || value.startsWith("//");
}

/** Path portion of an absolute URL: everything from the first slash after the authority. */
function pathAfterAuthority(value) {
  const scheme = ABSOLUTE_SCHEME.exec(value);
  const afterScheme = scheme ? value.slice(scheme[0].length) : value.slice(2);
  const slash = afterScheme.indexOf("/");
  return slash === -1 ? "" : afterScheme.slice(slash);
}

/** Path portion of any URL shape, absolute or relative, query and hash removed. */
function pathOf(value) {
  if (typeof value !== "string") return "";
  const trimmed = stripQueryAndHash(value.trim());
  return isAbsoluteUrl(trimmed) ? pathAfterAuthority(trimmed) : trimmed;
}

/**
 * Percent-encodes control characters so a canonical component can never contain a
 * newline. Without this a crafted path could forge extra canonical lines; raw
 * control characters are invalid in a URL path anyway.
 */
function encodeControlCharacters(path) {
  // eslint-disable-next-line no-control-regex
  return path.replace(/[\u0000-\u001f\u007f]/g, (char) => {
    const code = char.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0");
    return `%${code}`;
  });
}

/**
 * Origin-independent request path for the canonical string, query string excluded.
 *
 * Handles both shapes the shared client produces: an absolute URL, and a relative
 * path resolved against `baseURL` (`backendURL` is `http://host:8000/api/`, so
 * `semantic/sessions` becomes `/api/semantic/sessions`). Repeated slashes inside a
 * path are preserved rather than collapsed, since the server sees them verbatim;
 * only the base/relative join is de-duplicated.
 *
 * @param {string} [url] request URL, absolute or relative
 * @param {string} [baseUrl] Axios `baseURL`, if any
 * @returns {string} path beginning with `/`
 */
export function canonicalPath(url, baseUrl) {
  const requestPath = pathOf(url);

  const absolute =
    typeof url === "string" && isAbsoluteUrl(url.trim()) ? requestPath : null;

  let joined;
  if (absolute !== null) {
    // An absolute request URL ignores baseURL entirely, exactly as Axios does.
    joined = absolute;
  } else {
    const basePath = pathOf(baseUrl);
    if (basePath === "") {
      joined = requestPath;
    } else if (requestPath === "") {
      joined = basePath;
    } else {
      joined = `${basePath.replace(/\/+$/, "")}/${requestPath.replace(/^\/+/, "")}`;
    }
  }

  if (joined === "") return "/";
  return encodeControlCharacters(joined.startsWith("/") ? joined : `/${joined}`);
}

/* ------------------------------------------------------------------ *
 * Body serialization
 * ------------------------------------------------------------------ */

/** Global constructor test that tolerates environments where the type is absent. */
function isInstanceOf(value, globalName) {
  const ctor = typeof globalThis !== "undefined" ? globalThis[globalName] : undefined;
  return typeof ctor === "function" && value instanceof ctor;
}

/**
 * True for bodies that cannot be hashed without buffering a stream: `FormData`,
 * `Blob`/`File`, `ArrayBuffer` and its views, `URLSearchParams`, and Node/DOM
 * streams. The live case is the speech-transcribe multipart upload.
 *
 * **Open item for the backend team**: these requests are signed with the
 * empty-string body hash and marked `X-Body-Hash-Scope: none`. The backend must
 * either accept that, or exempt multipart requests from signing altogether.
 *
 * @param {unknown} data
 * @returns {boolean}
 */
export function isOpaqueBody(data) {
  if (data == null || typeof data === "string") return false;

  if (
    isInstanceOf(data, "FormData") ||
    isInstanceOf(data, "Blob") ||
    isInstanceOf(data, "File") ||
    isInstanceOf(data, "URLSearchParams") ||
    isInstanceOf(data, "ArrayBuffer") ||
    isInstanceOf(data, "ReadableStream")
  ) {
    return true;
  }

  if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(data)) return true;

  // Node streams (used by SSR/tooling callers) expose pipe().
  if (typeof data === "object" && typeof data.pipe === "function") return true;

  return false;
}

/**
 * Serializes a request body to the exact string that will be transmitted, so the
 * hash covers the bytes on the wire.
 *
 * Axios stringifies plain objects *after* the request interceptor runs, which would
 * leave the hash covering `[object Object]`. `signRequest` therefore serializes here
 * and assigns the result back to `config.data`.
 *
 * `undefined`, `null`, and `""` all map to `""`, so every empty body hashes to the
 * empty-string digest (Req 3.5). Opaque bodies also map to `""`; callers use
 * `isOpaqueBody` to tell the two cases apart.
 *
 * @param {unknown} data
 * @returns {string}
 */
export function canonicalBody(data) {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (isOpaqueBody(data)) return "";

  try {
    const serialized = JSON.stringify(data);
    // JSON.stringify returns undefined for functions and symbols.
    return typeof serialized === "string" ? serialized : "";
  } catch {
    // Circular or otherwise unstringifiable: Axios will fail on it too, so signing
    // must not be the thing that throws first.
    return "";
  }
}

/* ------------------------------------------------------------------ *
 * Canonical string
 * ------------------------------------------------------------------ */

/**
 * Builds the canonical string signed by the backend (Req 3.4).
 *
 * Exactly five `\n`-joined lines: upper-cased method, path, ISO 8601 UTC timestamp,
 * nonce, lowercase hex body hash. No trailing newline.
 *
 * @param {{ method: string, path: string, timestamp: string, nonce: string, bodyHash: string }} parts
 * @returns {string}
 */
export function buildCanonicalString({ method, path, timestamp, nonce, bodyHash }) {
  return [
    normalizeMethod(method),
    typeof path === "string" ? path : "",
    typeof timestamp === "string" ? timestamp : "",
    typeof nonce === "string" ? nonce : "",
    typeof bodyHash === "string" ? bodyHash : "",
  ].join("\n");
}

/* ------------------------------------------------------------------ *
 * Crypto primitives
 * ------------------------------------------------------------------ */

/**
 * The WebCrypto instance, or a clear configuration error.
 *
 * `crypto.subtle` requires a secure context, so plain HTTP on a non-localhost
 * origin has no SubtleCrypto at all. Tests install Node's WebCrypto in
 * `src/setupTests.js`.
 */
function getSubtle() {
  const webcrypto = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (!webcrypto || !webcrypto.subtle) {
    throw new HmacConfigurationError(
      "Web Crypto (crypto.subtle) is unavailable, so requests cannot be signed. " +
        "This usually means the app is served over plain HTTP from a non-localhost " +
        "origin; serve it over HTTPS to enable request signing."
    );
  }
  return webcrypto.subtle;
}

/** UTF-8 bytes of a string. */
function utf8(text) {
  if (typeof TextEncoder !== "function") {
    throw new HmacConfigurationError(
      "TextEncoder is unavailable, so request bodies cannot be encoded for signing."
    );
  }
  return new TextEncoder().encode(text);
}

/** Lowercase hex of a buffer. */
function toHex(buffer) {
  const bytes = new Uint8Array(buffer);
  let hex = "";
  for (let index = 0; index < bytes.length; index += 1) {
    hex += bytes[index].toString(16).padStart(2, "0");
  }
  return hex;
}

/**
 * Lowercase hex SHA-256 digest of a string.
 *
 * @param {string} [text]
 * @returns {Promise<string>} 64 hex characters
 */
export async function sha256Hex(text) {
  const digest = await getSubtle().digest("SHA-256", utf8(typeof text === "string" ? text : ""));
  return toHex(digest);
}

/**
 * Lowercase hex HMAC-SHA256 of `text` under `secret`.
 *
 * @param {string} secret
 * @param {string} text
 * @returns {Promise<string>} 64 hex characters
 */
export async function hmacSha256Hex(secret, text) {
  const subtle = getSubtle();
  const key = await subtle.importKey(
    "raw",
    utf8(typeof secret === "string" ? secret : ""),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const signature = await subtle.sign("HMAC", key, utf8(typeof text === "string" ? text : ""));
  return toHex(signature);
}

/* ------------------------------------------------------------------ *
 * Nonce and timestamp
 * ------------------------------------------------------------------ */

/**
 * Cryptographically random hex nonce (Req 3.3).
 *
 * The default of 16 bytes yields 32 hex characters, comfortably above the 16-character
 * floor; `byteLength` is clamped so a caller cannot ask for a nonce below it.
 *
 * @param {number} [byteLength=16]
 * @returns {string} lowercase hex, at least 16 characters
 */
export function createNonce(byteLength = DEFAULT_NONCE_BYTES) {
  const requested =
    typeof byteLength === "number" && Number.isFinite(byteLength)
      ? Math.floor(byteLength)
      : DEFAULT_NONCE_BYTES;
  const size = Math.max(MIN_NONCE_BYTES, requested);

  const webcrypto = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (!webcrypto || typeof webcrypto.getRandomValues !== "function") {
    throw new HmacConfigurationError(
      "crypto.getRandomValues is unavailable, so a request nonce cannot be generated."
    );
  }

  return toHex(webcrypto.getRandomValues(new Uint8Array(size)));
}

/**
 * Current UTC time as an ISO 8601 string ending in `Z` (Req 3.2).
 *
 * @param {Date|number|string} [now=new Date()]
 * @returns {string}
 */
export function createTimestamp(now = new Date()) {
  const date = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(date.getTime())) {
    throw new HmacConfigurationError(
      "An invalid timestamp was supplied for request signing."
    );
  }
  return date.toISOString();
}

/* ------------------------------------------------------------------ *
 * Secret resolution
 * ------------------------------------------------------------------ */

/** Trimmed non-empty string, or null. */
function readString(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The signing secret from `REACT_APP_HMAC_SECRET`, or null when it is unset
 * (Req 3.6).
 *
 * @param {Record<string, string|undefined>} [env=process.env]
 * @returns {string|null}
 */
export function readSigningSecret(env = process.env) {
  const source = env == null || typeof env !== "object" ? {} : env;
  return readString(source[HMAC_SECRET_ENV_VAR]);
}

/** One warning per process, so a local dev run is not flooded. */
let missingSecretWarned = false;

function warnMissingSecretOnce() {
  if (missingSecretWarned) return;
  missingSecretWarned = true;
  if (process.env.NODE_ENV === "test") return;
  // eslint-disable-next-line no-console
  console.warn(
    `[hmacSigner] ${HMAC_SECRET_ENV_VAR} is not set, so mutating requests are being ` +
      "sent unsigned. Set it in your .env file to sign requests locally."
  );
}

/** Clears the warning memo. Exists for tests; not used by application code. */
export function resetHmacWarningLog() {
  missingSecretWarned = false;
}

/* ------------------------------------------------------------------ *
 * Request signing
 * ------------------------------------------------------------------ */

/** Sets a header on both plain-object and AxiosHeaders `config.headers`. */
function setHeader(config, name, value) {
  if (!config.headers) config.headers = {};
  const headers = config.headers;
  if (typeof headers.set === "function") headers.set(name, value);
  else headers[name] = value;
}

/**
 * Attaches `X-Signature`, `X-Timestamp`, and `X-Nonce` to a mutating request
 * (Req 3.1-3.5, 3.7) and returns the same config object, mutated in place so it can
 * be used directly as an Axios request interceptor result.
 *
 * Behaviour:
 * - GET, HEAD, OPTIONS, and anything else outside `SIGNED_METHODS` are returned
 *   untouched (Req 3.7).
 * - With no secret configured, signing is skipped after one console warning, so
 *   local development against an unsigned backend keeps working (Req 3.6).
 * - Serializable bodies are stringified and written back to `config.data`, so the
 *   hash covers the transmitted bytes.
 * - Opaque bodies (multipart and friends) are signed with the empty-string body hash
 *   and marked `X-Body-Hash-Scope: none`; `config.data` is left alone.
 * - A missing `crypto.subtle` throws `HmacConfigurationError` rather than silently
 *   sending an unsigned mutation.
 *
 * @param {object} config Axios request config
 * @param {{ secret?: string|null, now?: Date|number, nonce?: string, env?: object }} [options]
 * @returns {Promise<object>} the same config
 */
export async function signRequest(config, options = {}) {
  if (config == null || typeof config !== "object") return config;
  if (!requiresSignature(config.method)) return config;

  const { secret: explicitSecret, now, nonce: explicitNonce, env } = options;
  const secret =
    readString(explicitSecret) ?? readSigningSecret(env === undefined ? process.env : env);

  if (!secret) {
    warnMissingSecretOnce();
    return config;
  }

  const opaque = isOpaqueBody(config.data);
  const body = canonicalBody(config.data);

  // Write the serialized body back so Axios transmits exactly what was hashed.
  // Nullish bodies are left alone: "" would turn "no body" into "empty body".
  if (!opaque && config.data != null) config.data = body;

  const bodyHash = await sha256Hex(opaque ? "" : body);
  const timestamp = createTimestamp(now === undefined ? new Date() : now);
  const nonce = readString(explicitNonce) ?? createNonce();
  const path = canonicalPath(config.url, config.baseURL);

  const signature = await hmacSha256Hex(
    secret,
    buildCanonicalString({ method: config.method, path, timestamp, nonce, bodyHash })
  );

  setHeader(config, SIGNATURE_HEADER, signature);
  setHeader(config, TIMESTAMP_HEADER, timestamp);
  setHeader(config, NONCE_HEADER, nonce);
  if (opaque) setHeader(config, BODY_HASH_SCOPE_HEADER, BODY_HASH_SCOPE_NONE);

  return config;
}
